// {"call":"range_history_all","data":{"network":"signet","start_timestamp":1713832677,"end_timestamp":1745368677}}

import { useEffect, useRef } from 'react';
import type { VaultProfile } from '@ducat-unit/core';
import { useQuery } from '@tanstack/react-query';

import { VALIDATOR_URL } from '@/constants';
import { getQueryClient } from '@/context/ReactQueryProvider';
import { useConfig } from '@/stores/useAppStore';
import { selectActiveVault, useVaultSelectionStore, vaultSelectionScope } from '@/stores/useVaultSelectionStore';

import { get } from '../api/api.helpers';
import { fetchAccountVaultList } from '../api/fetchers/validator.fetch';
import { QueryKeys } from '../api/query-keys';
import type { VaultsResponse } from '../api/types.validator';

// DEPRECATED.
// Docs: https://validator.ducatprotocol.com/api/swagger#/default/post_vault_list
export async function fetchVaultList(vault_pubkey: string) {
  // const params = {
  //   vault_pubkey,
  // };
  // const response: VaultsResponse = await post(url, params);

  const url = `${VALIDATOR_URL}/api/vault/pubkey/${vault_pubkey}`;
  const response: VaultsResponse = await get(url);

  return response;
}

// This replaces `useQueryVaultTokens`.
// Note: wallet runes pubkey is the vault pubkey.
export function useQueryAccountVaultList(vaultPubkey: string | undefined, options: { enabled?: boolean } = {}) {
  const config = useConfig();
  const enabled = !!vaultPubkey && !!config && options.enabled !== false;
  const current = useRef({ vaultPubkey, validatorUrl: config?.validatorUrl, enabled });
  current.current = { vaultPubkey, validatorUrl: config?.validatorUrl, enabled };
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const query = useQuery<VaultProfile[]>({
    queryKey: [QueryKeys.accountVaultList, vaultPubkey, config?.network, config?.validatorUrl],
    queryFn: async ({ signal }) => {
      const assertCurrent = () => {
        // The query can outlive this observer when an authenticated Quanta
        // observer still needs it. React Query cancels after the last leaves.
        if (signal.aborted || !enabled) throw Error('Vault lookup is unavailable for the current wallet');
      };
      assertCurrent();
      if (!vaultPubkey || !config) throw Error('No vault pubkey provided');
      const result = await fetchAccountVaultList(config.validatorUrl, vaultPubkey);
      assertCurrent();
      return result;
    },
    staleTime: 5 * 60 * 1000,
    enabled,
  });
  return {
    ...query,
    data: enabled ? query.data : undefined,
    isLoading: enabled && query.isLoading,
    refetch: (...args: Parameters<typeof query.refetch>) => {
      if (!mounted.current) return Promise.reject(new Error('Vault lookup observer is no longer active'));
      if (!current.current.enabled || current.current.vaultPubkey !== vaultPubkey || current.current.validatorUrl !== config?.validatorUrl)
        return Promise.reject(new Error('Vault lookup is unavailable for the current wallet'));
      return query.refetch(...args);
    },
  };
}

export const getVaultInfo = (vaultPubkey: string, validatorUrl: string) => {
  const queryClient = getQueryClient();
  // Older notifications lack an explicit root. Resolve their fallback using
  // the same endpoint and selection as the dashboard, never another network.
  const cachedVaultLists = queryClient.getQueriesData<VaultProfile[]>({
    queryKey: [QueryKeys.accountVaultList, vaultPubkey],
  });
  const cached = cachedVaultLists.find(([key, data]) => key[3] === validatorUrl && Array.isArray(data) && data.length > 0);
  const scope = vaultSelectionScope(validatorUrl, cached?.[0][2] as string | undefined, vaultPubkey);
  const selectedRoot = scope ? useVaultSelectionStore.getState().roots[scope] : undefined;
  const validatorVault = selectActiveVault(cached?.[1], selectedRoot);
  const vaultTxId = validatorVault?.root_txid || '';
  return { vaultTxId };
};
